import React, {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
} from 'react';
import {
    SplitFactoryProvider,
    useSplitClient,
} from '@splitsoftware/splitio-react';
import { FeatureFlag } from '../constants';
import { DonorWithWallet } from './DonorProvider/interfaces';
import { webConfig } from '../utils/webConfig';

const SDK_CONFIG = {
    core: {
        authorizationKey: webConfig.splitIoSdkKey,
        key: webConfig.getStaticUserId,
    },
};

type FeatureFlagContextValue = {
    isFeatureEnabled: (flag: FeatureFlag) => boolean;
};

const FeatureFlagContext = createContext<FeatureFlagContextValue>({
    isFeatureEnabled: () => false,
});

type FeatureFlagProviderProps = PropsWithChildren & {
    donorSession?: DonorWithWallet;
};

const FeatureFlagConsumerProvider: React.FC<FeatureFlagProviderProps> = ({
    donorSession,
    children,
}) => {
    const { splitKey, attributes } = React.useMemo(() => {
        const key = donorSession
            ? (donorSession.donor.id ?? webConfig.getStaticUserId)
            : webConfig.getStaticUserId;
        const attrs = {
            logged_in: !!donorSession?.donor?.id,
        };
        return { splitKey: key, attributes: attrs };
    }, [donorSession]);
    const { client, isReady } = useSplitClient({
        splitKey,
        attributes,
    });

    const isFeatureEnabled = useCallback(
        (flag: FeatureFlag) => {
            if (!isReady) {
                return false;
            }
            return client?.getTreatment(flag) === 'on';
        },
        [client, isReady],
    );

    return (
        <FeatureFlagContext.Provider value={{ isFeatureEnabled }}>
            {children}
        </FeatureFlagContext.Provider>
    );
};

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({
    donorSession,
    children,
}) => (
    <SplitFactoryProvider config={SDK_CONFIG}>
        <FeatureFlagConsumerProvider donorSession={donorSession}>
            {children}
        </FeatureFlagConsumerProvider>
    </SplitFactoryProvider>
);

export const useFeatureFlag = () => useContext(FeatureFlagContext);
